import React, { useState, useEffect, useContext, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { AppContext } from '../../App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import AdminLayout from '../../components/AdminLayout';
import {
  FaPlus, FaSave, FaTrash, FaEdit, FaCopy, FaEye, FaDownload, FaUpload,
  FaChevronDown, FaChevronRight, FaCog, FaList
} from 'react-icons/fa';
import {
  Drill, Wrench, Activity, Anchor, Truck, Settings, FileSearch, HardHat, Box, Layers
} from 'lucide-react';

const GROUP_ICONS = {
  'grp-1': Drill,
  'grp-2': Wrench,
  'grp-3': Activity,
  'grp-4': Anchor,
  'grp-5': Truck,
  'grp-6': Settings,
  'grp-7': FileSearch,
  'grp-8': HardHat,
};

export default function AdminTemplates() {
  const { i18n } = useTranslation();
  const { API } = useContext(AppContext);
  const [templates, setTemplates] = useState([]);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [expandedTemplates, setExpandedTemplates] = useState({});
  const fileInputRef = useRef(null);
  const [formData, setFormData] = useState({
    name_fr: '',
    name_en: '',
    description_fr: '',
    description_en: '',
    group_ids: [],
    active: true,
    position: 0,
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [templatesRes, groupsRes] = await Promise.all([
        axios.get(`${API}/templates`),
        axios.get(`${API}/categories/groups`),
      ]);
      setTemplates(templatesRes.data);
      setGroups(groupsRes.data);
      
      // Expand all by default
      const expanded = {};
      templatesRes.data.forEach(t => { expanded[t.id] = true; });
      setExpandedTemplates(expanded);
    } catch (error) {
      console.error('Error fetching templates:', error);
      toast.error('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      
      if (editingTemplate) {
        await axios.put(`${API}/templates/${editingTemplate.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Template mis à jour');
      } else {
        await axios.post(`${API}/templates`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Template créé');
      }
      
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    }
  };

  const resetForm = () => {
    setEditingTemplate(null);
    setFormData({
      name_fr: '',
      name_en: '',
      description_fr: '',
      description_en: '',
      group_ids: [],
      active: true,
      position: templates.length + 1,
    });
  };

  const handleEdit = (template) => {
    setEditingTemplate(template);
    setFormData({
      name_fr: template.name_fr || '',
      name_en: template.name_en || '',
      description_fr: template.description_fr || '',
      description_en: template.description_en || '',
      group_ids: template.group_ids || [],
      active: template.active !== false,
      position: template.position || 0,
    });
    setDialogOpen(true);
  };

  const handleDelete = async (templateId) => {
    if (!window.confirm('Supprimer ce template ?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/templates/${templateId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Template supprimé');
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleDuplicate = async (template) => {
    const newName = prompt('Nom du nouveau template (FR) :', `${template.name_fr} (copie)`);
    if (!newName) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/templates/${template.id}/duplicate?new_name_fr=${encodeURIComponent(newName)}&new_name_en=${encodeURIComponent(newName)}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Template dupliqué');
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la duplication');
    }
  };

  const handleExport = async () => {
    try {
      const response = await axios.get(`${API}/templates/export/json`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'templates_export.json');
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Export téléchargé');
    } catch (error) {
      toast.error('Erreur lors de l\'export');
    }
  };

  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);

    try {
      toast.loading('Import en cours...');
      const response = await axios.post(`${API}/templates/import/json`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.dismiss();
      toast.success(`Import terminé : ${response.data.imported} créés, ${response.data.updated} mis à jour`);
      fetchData();
    } catch (error) {
      toast.dismiss();
      toast.error('Erreur lors de l\'import');
      console.error('Import error:', error);
    }
    
    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const toggleTemplate = (templateId) => {
    setExpandedTemplates(prev => ({ ...prev, [templateId]: !prev[templateId] }));
  };

  const toggleGroupInForm = (groupId) => {
    setFormData(prev => {
      const current = prev.group_ids || [];
      if (current.includes(groupId)) {
        return { ...prev, group_ids: current.filter(id => id !== groupId) };
      } else {
        return { ...prev, group_ids: [...current, groupId] };
      }
    });
  };

  const getGroupName = (groupId) => {
    const group = groups.find(g => g.id === groupId);
    return group ? group.name_fr : groupId;
  };

  const openNewDialog = () => {
    resetForm();
    setDialogOpen(true);
  };

  return (
    <AdminLayout>
      <div className="min-h-screen bg-[#F5F5F5]" data-testid="admin-templates">
        <div className="p-4 md:p-6">
          {/* Header */}
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
            <div>
              <h1 className="text-2xl font-bold text-[#0A2540]">Gestion des Templates</h1>
              <p className="text-gray-600 text-sm mt-1">
                {templates.length} templates • Configurez les fiches produits par groupe
              </p>
            </div>
            
            <div className="flex flex-wrap gap-2">
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleImport}
                accept=".json"
                className="hidden"
              />
              <Button variant="outline" onClick={() => fileInputRef.current?.click()}>
                <FaUpload className="mr-2" /> Import JSON
              </Button>
              <Button variant="outline" onClick={handleExport}>
                <FaDownload className="mr-2" /> Export JSON
              </Button>
              <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogTrigger asChild>
                  <Button className="bg-[#635BFF] hover:bg-[#635BFF]/90" onClick={openNewDialog}>
                    <FaPlus className="mr-2" /> Nouveau template
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-lg">
                  <DialogHeader>
                    <DialogTitle>
                      {editingTemplate ? 'Modifier le template' : 'Nouveau template'}
                    </DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Nom (FR) *</Label>
                        <Input 
                          required 
                          value={formData.name_fr} 
                          onChange={(e) => setFormData({...formData, name_fr: e.target.value})} 
                        />
                      </div>
                      <div>
                        <Label>Name (EN) *</Label>
                        <Input 
                          required 
                          value={formData.name_en} 
                          onChange={(e) => setFormData({...formData, name_en: e.target.value})} 
                        />
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Description (FR)</Label>
                        <Input 
                          value={formData.description_fr} 
                          onChange={(e) => setFormData({...formData, description_fr: e.target.value})} 
                        />
                      </div>
                      <div>
                        <Label>Description (EN)</Label>
                        <Input 
                          value={formData.description_en} 
                          onChange={(e) => setFormData({...formData, description_en: e.target.value})} 
                        />
                      </div>
                    </div>

                    {/* Group Selection */}
                    <div>
                      <Label>Groupes de catégories liés</Label>
                      <div className="flex flex-wrap gap-2 p-3 bg-gray-50 rounded-lg mt-1">
                        {groups.map(group => {
                          const isSelected = (formData.group_ids || []).includes(group.id);
                          const IconComp = GROUP_ICONS[group.id] || Box;
                          return (
                            <button
                              key={group.id}
                              type="button"
                              onClick={() => toggleGroupInForm(group.id)}
                              className={`flex items-center gap-2 px-3 py-2 rounded-lg border transition-all ${
                                isSelected 
                                  ? 'bg-[#635BFF] text-white border-[#635BFF]' 
                                  : 'bg-white border-gray-200 hover:border-[#635BFF]'
                              }`}
                            >
                              <IconComp size={16} />
                              <span className="text-sm">{group.name_fr}</span>
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    {/* Active Toggle */}
                    <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                      <Switch
                        checked={formData.active}
                        onCheckedChange={(checked) => setFormData({...formData, active: checked})}
                      />
                      <Label className="cursor-pointer">Template actif</Label>
                    </div>

                    <div className="flex justify-end gap-3 pt-2">
                      <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                        Annuler
                      </Button>
                      <Button type="submit" className="bg-[#635BFF]">
                        <FaSave className="mr-2" /> Enregistrer
                      </Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>
            </div>
          </div>

          {/* Templates List */}
          {loading ? (
            <div className="p-12 text-center">
              <div className="w-8 h-8 border-4 border-[#635BFF] border-t-transparent rounded-full animate-spin mx-auto"></div>
            </div>
          ) : (
            <div className="space-y-4">
              {templates.map((template) => {
                const IconComp = GROUP_ICONS[template.group_ids?.[0]] || Layers;
                return (
                  <Card key={template.id} className={!template.active ? 'opacity-60' : ''}>
                    {/* Template Header */}
                    <button
                      onClick={() => toggleTemplate(template.id)}
                      className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors text-left"
                    >
                      <div className="flex items-center gap-4">
                        {expandedTemplates[template.id] ? (
                          <FaChevronDown className="text-gray-400" />
                        ) : (
                          <FaChevronRight className="text-gray-400" />
                        )}
                        <div className="w-12 h-12 bg-[#635BFF]/10 rounded-xl flex items-center justify-center">
                          <IconComp size={24} className="text-[#635BFF]" />
                        </div>
                        <div>
                          <h3 className="font-bold text-[#0A2540]">{template.name_fr}</h3>
                          <p className="text-sm text-gray-500">{template.description_fr}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="text-right text-sm">
                          <div className="text-[#635BFF] font-medium">{template.section_count || 0} sections</div>
                          <div className="text-gray-500">{template.field_count || 0} champs</div>
                        </div>
                        {!template.active && (
                          <span className="text-xs bg-gray-200 text-gray-600 px-2 py-1 rounded-full">Inactif</span>
                        )}
                      </div>
                    </button>

                    {/* Template Details */}
                    {expandedTemplates[template.id] && (
                      <CardContent className="border-t pt-4">
                        <div className="flex flex-wrap gap-4 mb-4">
                          <div>
                            <span className="text-xs text-gray-500 uppercase">Groupes liés</span>
                            <div className="flex flex-wrap gap-2 mt-1">
                              {(template.group_ids || []).map(gid => (
                                <span key={gid} className="text-sm bg-[#635BFF]/10 text-[#635BFF] px-2 py-1 rounded-full">
                                  {getGroupName(gid)}
                                </span>
                              ))}
                            </div>
                          </div>
                        </div>

                        {/* Sections Preview */}
                        {(template.sections || []).length > 0 && (
                          <div className="mb-4">
                            <span className="text-xs text-gray-500 uppercase">Sections</span>
                            <div className="flex flex-wrap gap-2 mt-1">
                              {template.sections.slice(0, 6).map(section => (
                                <span key={section.id} className="text-sm bg-gray-100 px-2 py-1 rounded">
                                  {section.name_fr}
                                </span>
                              ))}
                              {template.sections.length > 6 && (
                                <span className="text-sm text-gray-500">+{template.sections.length - 6}</span>
                              )}
                            </div>
                          </div>
                        )}

                        {/* Actions */}
                        <div className="flex flex-wrap gap-2 pt-2 border-t">
                          <Link to={`/admin/templates/${template.id}/fields`}>
                            <Button size="sm" variant="outline">
                              <FaCog className="mr-2" /> Gérer les champs
                            </Button>
                          </Link>
                          <Button size="sm" variant="outline" onClick={() => handleEdit(template)}>
                            <FaEdit className="mr-2" /> Modifier
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => handleDuplicate(template)}>
                            <FaCopy className="mr-2" /> Dupliquer
                          </Button>
                          <Button 
                            size="sm" 
                            variant="ghost" 
                            className="text-red-500 hover:text-red-700 hover:bg-red-50"
                            onClick={() => handleDelete(template.id)}
                          >
                            <FaTrash className="mr-2" /> Supprimer
                          </Button>
                        </div>
                      </CardContent>
                    )}
                  </Card>
                );
              })}
            </div>
          )}

          {/* Help */}
          <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-100">
            <h4 className="font-semibold text-blue-800 mb-2">💡 Guide des templates</h4>
            <ul className="text-sm text-blue-700 space-y-1">
              <li>• Chaque template est lié à un ou plusieurs groupes de catégories</li>
              <li>• Cliquez sur "Gérer les champs" pour ajouter/modifier les sections et champs</li>
              <li>• Les champs avec "Comparateur" activé apparaîtront dans le comparateur produits</li>
              <li>• Dupliquez un template pour créer une variante rapidement</li>
            </ul>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}
